import React, { useState } from "react";
import { Box, Container, Typography, Grid, IconButton, Stack } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

const testimonials = [
  {
    id: 1,
    image: "/assets/images/testimonial-zomato.png",
    quote:
      "“Ridexcity's electric bikes help me save a lot on fuel costs. Perfect for my long delivery hours every day.”",
    brand: "Zomato",
    role: "Delivery Partner",
  },
  {
    id: 2,
    image: "/assets/images/testimonial-swiggy.png",
    quote:
      "“The rental plans are flexible and affordable. I can easily choose a bike that fits my delivery schedule.”",
    brand: "Swiggy",
    role: "Delivery Partner",
  },
  {
    id: 3,
    image: "/assets/images/testimonial-zepto.png",
    quote:
      "“With Ridexcity's well-maintained EVs, I don't worry about breakdowns and I earn more with smooth deliveries.”",
    brand: "Zepto",
    role: "Delivery Partner",
  },
];

export default function TrustedAndLoved() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box
      id="trusted-and-loved"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: "background.default",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: { xs: 30, sm: 38, md: 44 },
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: "-0.02em",
            }}
          >
            Trusted{" "}
            <Box component="span" sx={{ color: "primary.main" }}>
              and Loved
            </Box>
          </Typography>
        </Box>

        {/* Testimonial Cards Grid */}
        <Grid container spacing={3.5} alignItems="stretch">
          {testimonials.map((item, idx) => (
            <Grid
              item
              xs={12}
              md={4}
              key={item.id}
              sx={{
                display: {
                  xs: idx === currentIndex ? "block" : "none",
                  md: "block",
                },
              }}
            >
              <Box
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: "20px",
                  overflow: "hidden",
                  height: "100%",
                  minHeight: 320,
                  display: "flex",
                  flexDirection: "row",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 10px 30px -10px rgba(20, 23, 26, 0.08)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "primary.main",
                    boxShadow: "0 18px 40px -12px rgba(27, 78, 216, 0.15)",
                  },
                }}
              >
                {/* Left Side: Rider Image */}
                <Box
                  sx={{
                    width: "45%",
                    minWidth: "45%",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={item.image}
                    alt={`${item.brand} ${item.role}`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>

                {/* Right Side: Quote & Details */}
                <Box
                  sx={{
                    width: "55%",
                    p: { xs: 2.5, sm: 3 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <FormatQuoteIcon
                      sx={{
                        fontSize: 42,
                        color: "primary.main",
                        transform: "rotate(180deg)",
                        mb: 0.5,
                        ml: -0.5,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: 13.5, sm: 14.5 },
                        fontWeight: 600,
                        color: "text.primary",
                        lineHeight: 1.55,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {item.quote.replace(/^“|”$/g, "")}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2.5 }}>
                    <Typography
                      sx={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: 18,
                        color: "primary.main",
                        lineHeight: 1.2,
                      }}
                    >
                      {item.brand}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: "text.secondary",
                        fontWeight: 500,
                        mt: 0.25,
                      }}
                    >
                      {item.role}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Carousel Navigation Buttons */}
        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 5 }}>
          <IconButton
            onClick={handlePrev}
            aria-label="Previous testimonial"
            sx={{
              bgcolor: "primary.main",
              color: "#fff",
              width: 44,
              height: 44,
              boxShadow: "0 4px 14px rgba(27, 78, 216, 0.3)",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "primary.dark",
                transform: "scale(1.08)",
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <IconButton
            onClick={handleNext}
            aria-label="Next testimonial"
            sx={{
              bgcolor: "primary.main",
              color: "#fff",
              width: 44,
              height: 44,
              boxShadow: "0 4px 14px rgba(27, 78, 216, 0.3)",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "primary.dark",
                transform: "scale(1.08)",
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Stack>
      </Container>
    </Box>
  );
}
